import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { catchError, defer, map, Observable, of, switchMap } from 'rxjs';
import { UnsubscribeService } from '@sb-core/services/unsubscribe.service';
import { UnsubscribeInfo } from '@sb-shared/models/unsubscribe-info';
import { LocaleService } from '@sb-shared/services/locale.service';

type UnsubscribeState = 'loading' | 'invalid' | 'ready' | 'done' | 'resubscribed';

/**
 * Organisation 2 holds the base en-GB labels every organisation falls back to (the DbUp label
 * convention). Used when the token does not resolve, so the page's own organisation is unknown.
 */
const BASE_LABEL_ORGANISATION_ID = 2;

/**
 * SB-14896: public GDPR unsubscribe landing page. Reached from the unsubscribe
 * link in every marketing email as `/Unsubscribe?token={guid}`. Loads anonymously
 * (AppComponent.loadAnnoymously skips the login, like ActivationComponent); the
 * opaque token is the only credential.
 */
@Component({
    selector: 'app-unsubscribe',
    templateUrl: './unsubscribe.component.html',
    styleUrl: './unsubscribe.component.scss',
    standalone: false
})
export class UnsubscribeComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private unsubscribeService = inject(UnsubscribeService);
  private localeService = inject(LocaleService);
  private cookieService = inject(CookieService);

  state: UnsubscribeState = 'loading';
  info?: UnsubscribeInfo;
  submitting = false;
  currentYear = new Date().getFullYear();

  private token = '';

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') ?? '';

    if (!this.token) {
      this.showInvalid();
      return;
    }

    this.unsubscribeService
      .getInfo(this.token)
      .pipe(switchMap((info) => this.loadLabels(info?.organisationId).pipe(map(() => info))))
      .subscribe({
        next: (info) => {
          this.info = info;
          // Already opted out (e.g. link clicked twice) -> show the confirmation state.
          this.state = info?.isOptedOut ? 'done' : 'ready';
        },
        error: () => this.showInvalid(),
      });
  }

  confirm(): void {
    if (this.submitting || !this.token) {
      return;
    }

    this.submitting = true;
    this.unsubscribeService.unsubscribe(this.token).subscribe({
      next: (info) => {
        this.info = info;
        this.state = 'done';
        this.submitting = false;
      },
      error: () => {
        this.state = 'invalid';
        this.submitting = false;
      },
    });
  }

  resubscribe(): void {
    if (this.submitting || !this.token) {
      return;
    }

    this.submitting = true;
    this.unsubscribeService.resubscribe(this.token).subscribe({
      next: (info) => {
        this.info = info;
        this.state = 'resubscribed';
        this.submitting = false;
      },
      error: () => {
        this.state = 'invalid';
        this.submitting = false;
      },
    });
  }

  backgroundImageUrl(): string {
    return `url(${window.EveryBuddy.WebAPI}/img/backgrounds/3.png)`;
  }

  /**
   * A token that does not resolve leaves the organisation unknown; the base labels still let the
   * page say so in words rather than as a raw SB_ tag.
   */
  private showInvalid(): void {
    this.state = 'invalid';
    this.loadLabels(BASE_LABEL_ORGANISATION_ID).subscribe();
  }

  /**
   * Anonymously there is no current organisation, so the translate pipe has no labels until the
   * organisation's own set is loaded — the route the other anonymous pages (Welcome, ExpiredToken)
   * take. Falls back to the base labels when the organisation is unknown. Best effort: deferred so
   * that even a synchronous failure is caught, and when it fails the page still renders.
   */
  private loadLabels(organisationId?: number): Observable<unknown> {
    return defer(() =>
      this.localeService.setOrganisationLanguage(organisationId || BASE_LABEL_ORGANISATION_ID, this.anonymousLanguage())
    ).pipe(catchError(() => of(null)));
  }

  /** The language picked on an anonymous page before, if any; otherwise the organisation's default. */
  private anonymousLanguage(): string | null {
    const stored = this.cookieService.get(this.localeService.cookieName);
    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
}
